import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// Mui
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useTheme } from '@mui/material/styles';
// Service
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { apiGetCurrentUser } from 'api/UserApi';
import RacingService from 'services/RacingService';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Height } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import useAuth from 'hooks/useAuth';

const AccountSettingsContainer = styled(Grid)(({ theme }) => ({
    minHeight: '550px',
    '.MuiTypography-h3': {
        margin: '20px 0px 20px 0px',
        //color: '#fff',
        fontSize: '22px'
    },
    '.mainContainer': {
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        display: 'flex',
        margin: '0px',
        width: 'calc(100%)',
        padding: '10px',
        table: {
            [theme.breakpoints.down('sm')]: {
                thead: { display: 'block' },
                'thead tr': {
                    position: 'absolute',
                    top: '-9999px',
                    left: '-9999px'
                },
                td: {
                    border: 'none',
                    // borderBottom: '1px solid #eee',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    position: 'relative',
                    paddingLeft: '50% !important',
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    display: 'block',
                    height: '44px'
                },

                'td:before': {
                    content: 'attr(data-title)'
                },
                'tbody tr': {
                    display: 'block',
                    borderBottom: '1px solid rgba(255,255,255,0.5)'
                },

                'td::before': {
                    position: 'absolute',
                    top: '9px',
                    left: '6px',
                    width: '45%',
                    paddingRight: '10px',
                    whiteSpace: 'nowrap',
                    textAlign: 'left'
                }
            }
        },
        '.leftContainer': {
            padding: '24px',
            '.MuiTypography-h4': {
                fontSize: '20px',
                color: '#fff',
                marginBottom: '8px'
            }
        },
        '.rightContainer': {
            borderLeft: '1px solid rgba(255,255,255,0.5)',
            ul: {
                padding: '0px',
                margin: '0px',
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                li: {
                    display: 'flex',
                    alignItems: 'center',
                    height: '50px',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '10px 20px',
                    borderBottom: '1px solid rgba(255,255,255,0.5)'
                }
            }
        },
        '.MuiTableCell-root ': { padding: '10px 16px' },
        '.MuiTableCell-head': { backgroundColor: theme.palette.secondary.main },
        '.MuiOutlinedInput-input': { color: '#fff' },
        '.deposit': {
            background: '#093de7',
            float: 'left',
            padding: '2px 8px',
            color: '#fff',
            fontWeight: '600',
            textTransform: 'capitalize'
        },
        '.sale': {
            background: '#D2042D',
            float: 'left',
            padding: '2px 8px',
            color: '#fff',
            fontWeight: '600',
            textTransform: 'capitalize'
        },
        '.withdraw': {
            background: '#fa5050',
            float: 'left',
            padding: '2px 8px',
            color: '#fff',
            fontWeight: '600',
            textTransform: 'capitalize'
        },
        '.payout': {
            background: '#0f9c12',
            float: 'left',
            padding: '2px 8px',
            color: '#fff',
            fontWeight: '600',
            textTransform: 'capitalize'
        },
        '.refund': {
            background: '#ff5800',
            float: 'left',
            padding: '2px 8px',
            color: '#fff',
            fontWeight: '600',
            textTransform: 'capitalize'
        }
    },
    '.transactiontext': {
        wordBreak: ' break-all',
        position: 'relative',
        top: '-8px',
        fontSize: '13px'
    }
}));

// Define the type for a transaction
interface Transaction {
    id: number;
    type: string;
    created: string;
    postBalance: number;
    preBalance: number;
    amount: number | null;
    comment: string;
}

//user/transaction/list
const MyWallet = () => {
    const theme = useTheme();
    const { user } = useAuth();
    // const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [pageSize, setPageSize] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [rows, setRows] = useState<Transaction[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    useEffect(() => {
        const getTransactions = async () => {
            const params = { userId: user?.UserId };
            const response = await RacingService.getSalePayoutTransactions(params);

            // Correct path
            const rawData = response.data.data.body.data;

            // Convert object → array
            const formattedData = Object.keys(rawData).flatMap((key) =>
                rawData[key].journals.map((journal: any) => ({
                    ...journal,
                    userName: rawData[key].userName,
                    cancelled: rawData[key].cancelled,
                    groupCode: key
                }))
            );

            setRowCount(formattedData.length);
            setRows(formattedData);
        };
        getTransactions();
    }, [page, pageSize, user]);
    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        setPageSize(event.target.value as number);
        setPage(0);
    };
    const startItem = page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, rowCount);
    const seq_page = page * pageSize;

    // Function to format the timestamp to dd/MM/yyyy
    const formatDate = (timestamp: string | number | Date) => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };

    return (
        <AccountSettingsContainer>
            <Grid container>
                <Grid item xs={12}>
                    <Typography
                        variant="h3"
                        sx={{
                            color: theme.palette.secondary.main,
                            [theme.breakpoints.down('sm')]: {
                                fontSize: '18px !important'
                            }
                        }}
                    >
                        Transactions
                    </Typography>
                </Grid>
            </Grid>
            <Grid container className="mainContainer">
                <TableContainer sx={{ background: '#050505' }} component={Paper}>
                    <Table sx={{}} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: theme.palette.grey[500], width: '10%', fontWeight: '800 !important' }} align="left">
                                    Sno
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.grey[500], width: '40%', fontWeight: '800 !important' }} align="left">
                                    Reference Id
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.grey[500], width: '25%', fontWeight: '800 !important' }} align="left">
                                    Transaction Date
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.grey[500], width: '15%', fontWeight: '800 !important' }} align="left">
                                    Amount
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.grey[500], width: '10%', fontWeight: '800 !important' }} align="left">
                                    Type
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography sx={{ color: '#fff', padding: '20px' }}>No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((transaction, key) => {
                                    return (
                                        <TableRow key={key}>
                                            <TableCell data-title="Sno" align="left">
                                                {seq_page + key + 1}
                                            </TableCell>
                                            <TableCell data-title="Reference Id" align="left">
                                                <Typography className="transactiontext">{transaction?.groupCode ?? ''}</Typography>
                                            </TableCell>
                                            <TableCell data-title="Transaction Date" align="left">
                                                {transaction.createdAt
                                                    ? formatDate(transaction.createdAt)
                                                    : formatDate(transaction.created)}
                                            </TableCell>
                                            <TableCell data-title="Expense" align="left">
                                                &#8369; {transaction?.betAmount ?? ''}
                                            </TableCell>
                                            <TableCell data-title="Type" align="left">
                                                <Typography className={`${transaction?.entryType}`}>{transaction?.entryType}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Grid container sx={{ paddingTop: '30px', display: { xs: 'none', sm: 'block' } }}>
                    <Grid item md={6} sm={6} xs={12}>
                        <Typography
                            variant="body2"
                            alignItems={'center'}
                            sx={{ padding: '12px 0px', fontWeight: 500, display: 'flex', height: '100%' }}
                        >
                            Showing {startItem}-{endItem} of {rowCount}
                        </Typography>
                    </Grid>
                    <Grid item md={6} sm={6} xs={12}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            sx={{ padding: '10px 0px 10px 0px !important', height: '30px' }}
                        >
                            <Box display="flex" alignItems="center">
                                <span>Rows per page: </span>
                                <Select
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    style={{ marginLeft: 10 }}
                                    notched={true}
                                    size="small"
                                    className="43434"
                                    sx={{ backgroundColor: 'inherit' }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: 'inherit',
                                                color: '#fff !important'
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </Select>
                            </Box>
                            <Button variant="outlined" color="primary" disabled={page === 0} onClick={() => handlePageChange(page - 1)}>
                                <NavigateBeforeIcon />
                            </Button>
                            <Box>
                                Page {page + 1} of {Math.ceil(rowCount / pageSize)}
                            </Box>
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={page >= Math.ceil(rowCount / pageSize) - 1}
                                onClick={() => handlePageChange(page + 1)}
                            >
                                <NavigateNextIcon />
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </AccountSettingsContainer>
    );
};

export default MyWallet;
